import sortBy from 'lodash/sortBy';
import {
    getBreakdownItems as getBreakdownItemsUtil,
    getTaxName,
    getTicketsCosts,
    getTicketsCostsByOrder,
} from '../../containers/pane/utils';
import { getTicketsAvailableOnSale } from '../../containers/ticketSelection/utils/ticketUtils';
import { Ticket, TicketCosts } from '../../models/tickets';
import { CheckoutState } from '../../state';
import { OrderNamespace } from '../../state/order';
import { getQuantityBySelectedDeliveryMethod } from '../../utils/deliveryMethod';
import { formatTicketsForOrderSummary } from '../../utils/tickets';
import {
    getCurrencyFormat,
    getIsEnableRefundFeeRetentionPolicyFF,
    getIsRegEvent,
} from '../app';
import {
    getSelectedDeliveryMethodsForDisplay,
    getTicketGroups,
} from '../deliveryMethod';
import { getCurrency } from '../event';
import { getShippingRates } from '../event/pureSelectors';
import {
    getIsTransferredOrder,
    hasMultipleRoyalties,
    haveSelectedTicketsShippingFee,
} from '../order';
import { getOrderCosts } from '../order/getOrderCosts';
import { getUnformattedOrderCost } from '../order/getUnformattedOrderCost';
import { isAddAttendeesOrder } from '../order/typedOrderSelectors';
import { getSelectedVirtualIncentives } from '../virtualIncentive';
import { getTicketsProps } from './getTicketsProps';
import { getTicketsById } from './pureSelectors';

const _getShippingFees = (tickets: Array<Ticket>): Array<{ value: number }> => {
    const shippingFees = Object.values(tickets)
        .filter(({ displayShippingFee }) => {
            return displayShippingFee?.value;
        })
        .map((ticket) => ticket.displayShippingFee);

    return shippingFees;
};

export const getBreakdownItems = (state: CheckoutState) => {
    const isAddAttendeesOrderValue = isAddAttendeesOrder(state);
    const isTransferredOrder = getIsTransferredOrder(state);
    const { selectedTickets } = getTicketsProps(state);
    const selectedTicketsOnSale =
        isAddAttendeesOrderValue || isTransferredOrder
            ? selectedTickets
            : getTicketsAvailableOnSale(selectedTickets);
    if (selectedTicketsOnSale.length) {
        const hasFeeBreakdown =
            hasMultipleRoyalties(state) ||
            isTransferredOrder ||
            haveSelectedTicketsShippingFee(state);
        const orderCosts = getOrderCosts(state, hasFeeBreakdown);
        const ticketsById = getTicketsById(state);
        const ticketGroups = getTicketGroups(state);
        const selectedDeliveryMethods =
            getSelectedDeliveryMethodsForDisplay(state);
        const currencyFormat = getCurrencyFormat(state) || '';
        const currency = getCurrency(state) || '';
        const enableRefundFeeRetentionPolicy =
            getIsEnableRefundFeeRetentionPolicyFF(state);
        const installments = state.order
            ? (state.order as OrderNamespace).displayInstallments
            : null;
        const isRegEvent = Boolean(getIsRegEvent(state));
        const shippingRates = getShippingRates(state);
        const selectedVirtualIncentives = getSelectedVirtualIncentives(state);
        const unformattedOrderCost = getUnformattedOrderCost(state);
        const flattenedTickets = formatTicketsForOrderSummary(
            selectedTicketsOnSale,
        );

        // UX decision to sort tickets with admission first and then addons
        const sortedTickets = sortBy(flattenedTickets, [
            (ticket) => ticket.category === 'add_on',
        ]);

        // Not need to check on the selected tickets because the GTS tax info is shared among all the ticket classes
        const eventTaxName = getTaxName(
            ticketsById,
            selectedTicketsOnSale,
            orderCosts,
        ).toString();

        // Need to obtain how many tickets were selected by every delivery method
        const quantitySelectedDeliveryMethods =
            getQuantityBySelectedDeliveryMethod(
                ticketGroups,
                selectedDeliveryMethods,
            );

        // Get costs from the tickets selected (before user clicks Checkout)
        let ticketsCosts: TicketCosts = getTicketsCosts(
            sortedTickets,
            hasFeeBreakdown,
        );

        // If there is an order, get the costs from the order (after user clicks Checkout)
        if (orderCosts) {
            ticketsCosts = getTicketsCostsByOrder(orderCosts, hasFeeBreakdown);
        }

        // When we have an existing order, the payment is processed offline, and no fees are collected: we should not diplay any to the user.
        if (isAddAttendeesOrderValue) {
            ticketsCosts.fees = 0;
        }
        const shippingFees = _getShippingFees(selectedTickets);

        return getBreakdownItemsUtil({
            ...ticketsCosts,
            currency,
            currencyFormat,
            enableRefundFeeRetentionPolicy,
            eventTaxName,
            installments,
            isRegEvent,
            isTransferredOrder,
            orderCosts,
            quantitySelectedDeliveryMethods,
            selectedDeliveryMethods,
            shippingFees,
            shippingRates,
            selectedVirtualIncentives,
            unformattedOrderCost,
        });
    }
    return [];
};
